/**
 * 관세율 상세 — 정적 참조 데이터 (재크롤 불필요, 관세법 시행령 고정값).
 *
 *  - TARIFF_PRIORITY  : 세율적용 우선순위 (UNIPASS UI-ULS-0201-009Q · 관세법 제50조)
 *  - RATE_CODE_GUIDE  : 관세율부호(구분기호) 설명 (CLHS 기본관세 페이지 · 관세율부호표)
 *  - SPECIAL_PERIOD_PREFIXES : 적용기간이 연중 가변이라 기본 연도값을 쓰면 안 되는 코드군
 *
 *  ⚠ 세율 표시는 오표시가 치명적 — 날짜/단위당세액은 "표시 전용"이며 계산에 쓰지 않는다.
 *    정확한 적용개시·만료일은 관세청 UNIPASS 원문(009Q) 기준. 여기 연도값은 안내용.
 */

// ── 세율적용 우선순위 (낮은 순위 번호 = 먼저 적용) ─────────────────────────
//   관세법 제50조 [별표] 세율적용 우선순위. UNIPASS 009Q 화면과 동일 구조.
export const TARIFF_PRIORITY = [
  {
    order: 1,
    apply: "가장 우선하여 적용",
    items: [
      { code: "I", name: "덤핑방지관세 (+)" },
      { code: "M", name: "상계관세" },
      { code: "J", name: "보복관세" },
      { code: "K", name: "긴급관세" },
      { code: "+", name: "특정물품 긴급관세" },
      { code: "T", name: "농림축산물 특별긴급관세" },
      { code: "L", name: "조정관세 (제69조 제2호)" },
    ],
  },
  {
    order: 2,
    apply: "3~7순위보다 낮은 경우 우선 적용",
    items: [
      { code: "F***", name: "FTA 협정관세 (한ㆍEU/미국/중국/아세안/RCEP 등)" },
    ],
  },
  {
    order: 3,
    apply: "4~7순위보다 낮은 경우 우선 적용",
    items: [
      { code: "C", name: "WTO 일반양허관세" },
      { code: "E1/E2/E3", name: "아ㆍ태 협정 양허관세 (APTA)" },
      { code: "G", name: "유엔무역개발회의 개발도상국간 협정관세 (GSTP)" },
      { code: "F", name: "특정국가와의 관세협상 국제협력관세" },
      { code: "N", name: "편익관세" },
      { code: "R", name: "최빈개발도상국 특혜관세" },
    ],
  },
  {
    order: 4,
    apply: "5~7순위보다 우선 적용",
    items: [
      { code: "L", name: "조정관세 (제69조 제1ㆍ3ㆍ4호)" },
      { code: "P", name: "할당관세" },
    ],
  },
  {
    order: 5,
    apply: "6~7순위보다 낮은 경우 우선 적용",
    items: [{ code: "G", name: "일반특혜관세 (GSP)" }],
  },
  {
    order: 6,
    apply: "7순위보다 우선 적용",
    items: [{ code: "B", name: "잠정세율" }],
  },
  {
    order: 7,
    apply: "최종 적용 (위 세율이 없을 때)",
    items: [{ code: "A", name: "기본세율" }],
  },
];

// ── 관세율부호(구분기호) 설명 ──────────────────────────────────────────────
//   코드 → { name(공식명), kind(분류), desc(부연) }. FTA(F***)는 동적이라 prefix 안내.
export const RATE_CODE_GUIDE = {
  A: {
    name: "기본세율",
    kind: "기본",
    desc: "하나의 품목번호에 1개의 기본세율이 적용되는 물품",
  },
  A1: {
    name: "기본세율(고세율)",
    kind: "기본",
    desc: "하나의 품목번호에 서로 다른 기본세율이 있는 경우 최고세율 적용물품",
  },
  B: {
    name: "잠정세율",
    kind: "잠정",
    desc: "기본세율에 우선하여 한시 적용되는 세율",
  },
  C: {
    name: "WTO협정세율",
    kind: "양허",
    desc: "WTO 회원국에 적용하는 일반양허관세율",
  },
  E1: {
    name: "아ㆍ태협정 일반양허관세",
    kind: "양허",
    desc: "APTA 일반 양허세율",
  },
  E2: {
    name: "아ㆍ태협정 방글라데시 양허관세",
    kind: "양허",
    desc: "APTA 방글라데시 특별 양허",
  },
  E3: {
    name: "아ㆍ태협정 라오스 양허관세",
    kind: "양허",
    desc: "APTA 라오스 특별 양허",
  },
  G: {
    name: "일반특혜관세(GSP)",
    kind: "특혜",
    desc: "개발도상국 일반특혜관세",
  },
  R: {
    name: "최빈개발도상국 특혜관세",
    kind: "특혜",
    desc: "최빈개도국 대상 특혜세율",
  },
  U: { name: "북한산", kind: "특혜", desc: "남북교역물품(북한산) 적용세율" },
  N: {
    name: "편익관세",
    kind: "편익",
    desc: "조약 미체결국 중 편익 부여국 적용세율",
  },
  P: {
    name: "할당관세",
    kind: "할당/탄력",
    desc: "물량 범위 내 낮은 세율 (초과분은 기본세율)",
  },
  L: {
    name: "조정관세",
    kind: "할당/탄력",
    desc: "국내산업 보호 등을 위한 탄력관세",
  },
  I: {
    name: "덤핑방지관세",
    kind: "탄력(가산)",
    desc: "덤핑물품에 부과하는 가산세 (+)",
  },
  M: {
    name: "상계관세",
    kind: "탄력(가산)",
    desc: "보조금 받은 물품에 부과하는 가산세 (+)",
  },
  J: {
    name: "보복관세",
    kind: "탄력(가산)",
    desc: "교역상대국 조치에 대한 보복 가산세 (+)",
  },
  K: {
    name: "긴급관세",
    kind: "탄력(가산)",
    desc: "수입급증 시 한시 부과하는 가산세 (+)",
  },
  T: {
    name: "농림축산물 특별긴급관세",
    kind: "탄력(가산)",
    desc: "농림축산물 물량/가격 기준 긴급세 (+)",
  },
  W1: {
    name: "WTO일반양허관세(별표1나)",
    kind: "양허",
    desc: "WTO 양허규정 별표1나",
  },
  W2: {
    name: "WTO일반양허관세(별표1나)",
    kind: "양허",
    desc: "WTO 양허규정 별표1나",
  },
};

// ── 적용기간이 연중 가변 → 기본 연도값(1/1~12/31)을 표시하면 안 되는 코드 prefix ──
//   할당(P)/조정(L)/긴급(K,T)/덤핑·상계·보복(I,M,J)은 연중 개시·만료가 달라 "—" 처리.
export const SPECIAL_PERIOD_PREFIXES = ["P", "L", "K", "T", "I", "M", "J"];

export function isSpecialPeriodCode(code) {
  if (!code) return false;
  const c = String(code).toUpperCase();
  // FTA(F***)는 제외 — 권역 협정세율은 연 단위 적용
  if (c.startsWith("F") && c.length > 1) return false;
  return SPECIAL_PERIOD_PREFIXES.some((p) => c === p || c.startsWith(p));
}

// ── raw 문자열 → 단위당세액(종량/선택세 고정금액) 추출 (표시 전용, 계산 금지) ──
//   "27% 또는 1,879원" → "1,879원"  /  "8%" → null  /  "0~25.04%" → null
export function extractFixedAmount(raw) {
  if (!raw || typeof raw !== "string") return null;
  // "또는" 뒤의 금액(원/달러 등) 부분
  const m = raw.match(/또는\s*([\d,]+\s*(?:원|달러|위안|엔)[^\s,]*)/);
  if (m) return m[1].replace(/\s+/g, "");
  // "또는" 없이 금액만 있는 종량세 (드뭄)
  const m2 = raw.match(/^([\d,]+\s*(?:원|달러)[^\s,]*)$/);
  if (m2) return m2[1].replace(/\s+/g, "");
  return null;
}
